import { useState } from 'react';
import { integrationApi, payablesApi } from '../../api/services';
import type { IntegrationOperationStatusDto } from '../../api/types';
import { Alert, Badge, Button, Field, Form, Loadable, Modal, Select, Table, useAction, useAsync } from '../../ui/components';
import { TemplateSelect } from '../../ui/pickers';
import { isIntegrationOperation } from './integrationScope';

// ================================================================ Operaciones

const SOURCE: Record<string, { label: string; tone: 'ok' | 'warn' | 'neutral' }> = {
  DB: { label: 'Configurada', tone: 'ok' }, APPSETTINGS: { label: 'Configuración del servidor', tone: 'warn' }, NONE: { label: 'Sin configurar', tone: 'neutral' },
};

/** Asignación de plantilla por operación (CxC, CxP, bancos y planilla). Las de integración se configuran en Integración. */
export function OperationAssignments() {
  const ops = useAsync(() => integrationApi.operations().then((rows) => rows.filter((o) => !isIntegrationOperation(o))), []);
  const [editing, setEditing] = useState<{ op: IntegrationOperationStatusDto; byPaymentType: boolean } | null>(null);
  const { run, busy } = useAction();
  return (
    <>
      <Alert>
        Cada operación de Cuentas por cobrar, Cuentas por pagar, Bancos y Planilla usa la plantilla asignada aquí. La columna
        "Montos" indica qué montos trae la operación para asignarlos a las líneas de la plantilla. La facturación, los pagos,
        las cajas y el inventario se configuran en Configuración → Integración.
      </Alert>
      <Loadable state={ops}>
        {(rows) => (
          <Table rows={rows} rowKey={(o) => o.operation} columns={[
            { key: 'n', title: 'Operación', render: (o) => (o.paymentType
              ? <span style={{ paddingLeft: 16, display: 'inline-block' }}>↳ {o.name}</span>
              : <strong>{o.name}</strong>) },
            { key: 's', title: 'Estado', render: (o) => <Badge tone={o.source === 'DB' && o.templateActive === false ? 'bad' : SOURCE[o.source].tone}>{o.source === 'DB' && o.templateActive === false ? 'Plantilla inactiva' : SOURCE[o.source].label}</Badge> },
            { key: 't', title: 'Plantilla', render: (o) => (o.templateCode ? `${o.templateCode} · ${o.templateDescription ?? ''}` : '') },
            { key: 'c', title: 'Montos', render: (o) => <small>{o.concepts}</small> },
            {
              key: 'x', title: '', render: (o) => (
                <>
                  <Button kind="link" onClick={() => setEditing({ op: o, byPaymentType: false })}>Asignar…</Button>
                  {o.supportsPaymentTypes && !o.paymentType && <Button kind="link" onClick={() => setEditing({ op: o, byPaymentType: true })}>Por tipo de pago…</Button>}
                  {o.source === 'DB' && <Button kind="link" busy={busy} onClick={() => run(() => integrationApi.removeOperation(o.operation), { confirm: `Quitar la plantilla asignada a "${o.name}"? La plantilla no se borra.`, danger: true, success: 'Asignación eliminada.' }).then(ops.reload)}>Quitar</Button>}
                </>
              ),
            },
          ]} />
        )}
      </Loadable>
      {editing && <AssignOperation op={editing.op} byPaymentType={editing.byPaymentType} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); ops.reload(); }} />}
    </>
  );
}

function AssignOperation({ op, byPaymentType, onClose, onSaved }: { op: IntegrationOperationStatusDto; byPaymentType: boolean; onClose: () => void; onSaved: () => void }) {
  const paymentTypes = useAsync(() => (byPaymentType ? payablesApi.paymentTypes() : Promise.resolve([])), [byPaymentType]);
  const [paymentType, setPaymentType] = useState<string>('');
  const [template, setTemplate] = useState<number | null>(byPaymentType ? null : op.templateCode ?? null);
  const { run, busy } = useAction();
  const save = () => run(async () => {
    if (byPaymentType && !paymentType) throw new Error('Seleccione el tipo de pago.');
    if (!template) throw new Error('Seleccione la plantilla.');
    await integrationApi.setOperation(byPaymentType ? `${op.baseOperation}@${paymentType}` : op.operation, template, null);
    onSaved();
  }, { success: 'Plantilla asignada.' });
  return (
    <Modal title={byPaymentType ? `Plantilla por tipo de pago: ${op.name}` : `Asignar plantilla: ${op.name}`} onClose={onClose}>
      <Form onSubmit={save}>
        <p><small>Montos que envía la API: {op.conceptList.length ? op.conceptList.map((c) => op.conceptLabels?.[c] ?? c).join(', ') : op.concepts}</small></p>
        {byPaymentType && (
          <Field label="Tipo de pago">
            <Select value={paymentType} onChange={setPaymentType} placeholder="Seleccione" options={(paymentTypes.data ?? []).map((t) => ({ value: t.id.trim(), label: `${t.id.trim()} · ${t.description}` }))} />
          </Field>
        )}
        <Field label="Plantilla"><TemplateSelect moduleCode={op.moduleCode} value={template} onChange={setTemplate} allowDefault={false} /></Field>
        <div className="acc-actions"><Button onClick={onClose}>Cancelar</Button><Button kind="primary" type="submit" busy={busy}>Guardar</Button></div>
      </Form>
    </Modal>
  );
}
